<template>
    <div class="import-feedback-drawer-guide-step">
        <div class="import-feedback-drawer-guide-step__header">
            <div class="import-feedback-drawer-guide-step__title">
                Select {{ pageTitle }}
            </div>
        </div>
        <div class="import-feedback-drawer-guide-step__filters">
            <guide-expansion-filters
                :filters-show-hide-config="filtersShowHideConfig"
                :expansion-filters-config="expansionFiltersConfig"
                :guide-list="tableData"
                :user="user"
                :filters-config="filtersConfig"
                @on-update-filters="setFilters"
                @on-update-group-ids="updateGroupIds" />
        </div>
        <div
            class="import-feedback-drawer-guide-step__content"
            :class="{ 'import-feedback-drawer-guide-step__content--nps': isNPSType }"
            data-cy="import-feedback-guide-table">
            <pendo-table
                ref="importGuidesTable"
                class="import-feedback-drawer-guide-step__table"
                :class="{ 'import-feedback-drawer-guide-step__table--loading': tableStatus === 'loading' }"
                :cell-class-name="getCellClassName"
                :default-sort="defaultSort"
                highlight-current-row
                :height="600"
                resizable
                :title="tableTitle"
                row-key="id"
                :data="tableData"
                :filters="filters"
                :status="tableStatus"
                :columns="columnConfig"
                manage-columns
                @link-click="selectGuide">
                <template #status>
                    <pendo-status-overlay
                        v-if="tableStatus === 'loading'"
                        class="import-feedback-drawer-guide-step__loading"
                        :state="tableStatus"
                        :options="{
                            loadingText: 'Loading...',
                        }" />
                </template>
                <template #autoCreateFeedback="{ row }">
                    <div
                        v-if="!row.autoCreateFeedback"
                        class="import-feedback-drawer-guide-step__autocreate">
                        <pendo-icon
                            type="X"
                            size="18"
                            stroke-width="2"
                            stroke="var(--palette-red-100)" />
                        No
                    </div>
                    <div
                        v-if="row.autoCreateFeedback"
                        class="import-feedback-drawer-guide-step__autocreate">
                        <pendo-icon
                            type="check"
                            size="18"
                            stroke-width="2"
                            stroke="var(--palette-green-90)" />
                        Yes
                    </div>
                </template>
                <template #group="{ row }">
                    <pendo-group-cell :row="row" />
                </template>
                <template #app="{ row } ">
                    <div>
                        <pendo-app-cell :row="row.appItem" />
                    </div>
                </template>
                <template #state="{ row }">
                    <pendo-guide-status-cell
                        :row="sanitizeState(row)"
                        :interactive="false" />
                </template>
            </pendo-table>
            <div class="import-feedback-drawer-guide-step__selected-wrapper">
                <import-feedback-drawer-guide-sample-card
                    :type="type"
                    :selected-guide="selectedGuide"
                    :selected-guide-responses="selectedGuideResponses"
                    :selected-guide-response-count="selectedGuideResponseCount"
                    :fetching-source-config="fetchingSourceConfig"
                    :selected-poll-question="selectedPollQuestion"
                    @change-poll-question="changePollQuestion"
                    @open-guide="openGuideNewTab" />
            </div>
        </div>
    </div>
</template>

<script>
import {
    PendoIcon,
    PendoTable,
    PendoStatusOverlay,
    PendoGuideStatusCell,
    PendoLoading,
    PendoGroupCell,
    PendoAppCell,
} from '@pendo/components';
import moment from '@utils/moment-wrapper';
import { sanitizeState } from '@pendo/services/Formatters';
import GuideExpansionFilters from '@/guides/components/guide-management-overview-tab/guide-expansion-filters/guide-expansion-filters.vue';
import { mapState, mapGetters } from 'vuex';
import ImportGuidesAggregation from '@/voc/aggregations/import-guides/import-guides.aggregation';
import { connectAggregationToState, withComponent } from '@pendo/agg-connect';
import { LOADING_STATES } from '@/constants';
const { LOADING, REJECTED, RESOLVED } = LOADING_STATES;
import { getNoGuideCategoryOption } from '@/common/guide-category-multiselect/guide-category-multiselect-cell.vue';
import find from 'lodash/find';
import { runAggregationOnce } from '@/utils/aggregation';
import ImportGuidePollResponseSummaryAggregation from '@/voc/aggregations/import-guide-poll-response-summary/import-guide-poll-response-summary.aggregation.js';
import ImportNPSPollResponseSummaryAggregation from '@/voc/aggregations/import-nps-poll-response-summary/import-nps-poll-response-summary.aggregation.js';

import { getGuideFieldMapping, getSourceConfig } from '@/voc/api/sources.api';
import ImportFeedbackDrawerGuideSampleCard from '@/voc/components/drawers/import-feedback-drawer/components/import-feedback-drawer-guide-sample-card/import-feedback-drawer-guide-sample-card.vue';
import { formatPollQuestion, aiAnalyzeFlag, roundResponseCount } from '@utils/voc-polls.utils';

export default {
    name: 'ImportFeedbackDrawerGuideStep',
    components: {
        PendoIcon,
        PendoTable,
        PendoStatusOverlay,
        PendoGuideStatusCell,
        GuideExpansionFilters,
        PendoGroupCell,
        PendoAppCell,
        ImportFeedbackDrawerGuideSampleCard,
    },
    directives: {
        PendoLoading,
    },
    props: {
        type: {
            type: String,
            default: 'guide',
        },
    },
    emits: ['poll-count-update', 'guide-selected', 'poll-selected'],
    data () {
        return {
            columnConfig: [
                {
                    prop: 'name',
                    label: 'Guide name',
                    fixed: true,
                    type: 'link',
                    width: 400,
                    visible: true,
                },
                {
                    prop: 'autoCreateFeedback',
                    slot: 'autoCreateFeedback',
                    label: 'Connected source',
                    visible: true,
                },
                {
                    prop: 'group.name',
                    slot: 'group',
                    label: 'Product area',
                    visible: true,
                },
                {
                    prop: 'state',
                    slot: 'state',
                    label: 'Status',
                    minWidth: 150,
                    visible: true,
                },
                ...(this.type === 'guide'
                    ? [
                        {
                            prop: 'description',
                            label: 'Description',
                            slot: 'description',
                            visible: true,
                        },
                    ]
                    : []),
                {
                    prop: 'appId',
                    label: 'App',
                    slot: 'app',
                    minWidth: 150,
                    visible: true,
                },
                {
                    prop: 'lastUpdatedBy',
                    label: 'Last updated by',
                    visible: true,
                },
                {
                    prop: 'lastUpdatedAt',
                    label: 'Last updated time',
                    formatter: (row) => moment(row.lastUpdatedAt).format('MMM D, YYYY, h:mm a z'),
                    width: 195,
                    sortable: true,
                    visible: true,
                },
            ],
            defaultSort: {
                prop: 'lastUpdatedAt',
                order: 'descending',
            },
            filtersShowHideConfig: {
                segmentChooser: false,
                accountChooser: false,
                timeSeries: false,
                appChooser: true,
                allowAllTime: false,
                languageChooser: false,
            },
            expansionFiltersConfig: {
                showActivations: false,
                showCategories: true,
                showExpirations: false,
                showGuideTypes: false,
                showDeviceTypes: false,
                showCreators: true,
                showProductAreas: true,
                showManagedIn: false,
            },
            filters: null,
            tableStatus: 'loading',
            tableData: [],
            groupIds: [],
            selectedGuide: null,
            selectedGuideResponses: [],
            selectedGuideResponseCount: null,
            selectedPollQuestion: null,
            fetchingSourceConfig: false,
        };
    },
    computed: {
        ...mapState('auth', ['user']),
        ...mapGetters({
            appId: 'globalFilters/appId',
            guideCategories: 'guideCategories/list',
            getAppById: 'apps/getAppById',
        }),
        filtersConfig () {
            return {
                filterType: 'guide',
                appChooser: {
                    filterNonIntegratedApps: true,
                },
            };
        },
        appIdFormatted () {
            return this.appId?.length > 0 ? this.appId : 'expandAppIds("*")';
        },
        tableTitle () {
            return this.isNPSType ? 'NPS' : 'Guides';
        },
        isNPSType () {
            return this.type === 'NPS';
        },
        pageTitle () {
            return this.isNPSType ? 'NPS' : 'guide';
        },
    },
    watch: {
        guideCategories () {
            if (this.tableData?.length && this.guideCategories?.length) {
                this.attachCategories();
            }
        },
    },
    created () {
        if (this.$route?.meta?.search) {
            this.$route.meta.search = '';
        }
        this.setupImportGuidesAgg();
        this.resetSelectedGuide();
    },
    methods: {
        async selectGuide ({ row }) {
            this.$refs.importGuidesTable.toggleRowActive(row);
            this.fetchingSourceConfig = true;
            this.$emit('poll-count-update', null);
            this.selectedGuide = row;
            await this.assignPollQuestion(row);
            this.fetchingSourceConfig = false;
            this.$emit('poll-selected', this.selectedPollQuestion);
            this.$emit('guide-selected', this.selectedGuide);
            this.updatePollResponsesCard();
        },
        sanitizeState (guide) {
            return sanitizeState(guide);
        },
        setFilters (filters) {
            this.filters = filters;
        },
        getCellClassName ({ column }) {
            if (column.columnKey === 'name') {
                return 'import-feedback-guide-table__cell--name';
            }

            return `import-feedback-guide-table__cell--${column.columnKey}`;
        },
        initImportGuidesAgg () {
            const sourcesOfState = {
                component: this,
            };

            return connectAggregationToState(
                ImportGuidesAggregation,
                withComponent({
                    appId: 'appIdFormatted',
                    groupIds: 'groupIds',
                    type: 'type',
                }),
            )(sourcesOfState);
        },
        setupImportGuidesAgg () {
            if (this.importGuidesAgg$) this.importGuidesAgg$.unsubscribe();
            const importGuidesAgg$ = this.initImportGuidesAgg();
            this.importGuidesAgg$ = importGuidesAgg$.subscribe(this.onImportGuidesAggResponse);
        },
        onImportGuidesAggResponse ({ status, value }) {
            switch (status) {
                case RESOLVED:
                    this.tableData = this.processGuides(value);
                    this.tableStatus = status;
                    break;
                case LOADING:
                    this.tableStatus = status;
                    break;
                case REJECTED:
                    this.tableStatus = status;
                    break;
            }
        },
        updateGroupIds (groupIds) {
            this.groupIds = groupIds;
        },
        processGuides (guides) {
            return guides.map((guide) => {
                if (guide.group === null) {
                    return {
                        ...guide,
                        group: {
                            num: '',
                            color: '',
                            id: '',
                            name: '',
                        },
                        appItem: { app: this.getAppById(guide.appId) },
                    };
                }

                return { ...guide, appItem: { app: this.getAppById(guide.appId) } };
            });
        },
        attachCategories () {
            for (const guide of this.tableData) {
                guide.category = { ...getNoGuideCategoryOption() };
                for (const guideCategory of this.guideCategories) {
                    if (
                        guideCategory.items?.length &&
                        find(guideCategory.items, { itemId: guide.id, itemType: 'Guide' })
                    ) {
                        guide.category = { id: guideCategory.id, name: guideCategory.name };
                        break;
                    }
                }
            }
        },
        openGuideNewTab () {
            window.open(
                this.$router.pendo.toPath({ name: 'GuideDetails', params: { guideId: this.selectedGuide.id } }),
            );
        },
        async updatePollResponsesCard () {
            this.selectedGuideResponses = null;
            this.selectedGuideResponseCount = null;
            this.$emit('poll-count-update', null);

            const pollIds = this.selectedGuide.polls.map((poll) => poll.id);
            let guideResponseAggData;
            if (this.type === 'NPS') {
                const scorePoll = this.selectedGuide.polls.find((poll) => poll.attributes?.type === 'NPSRating');
                const npsFollowUpPollId = scorePoll?.attributes?.followUp;
                guideResponseAggData = await runAggregationOnce(ImportNPSPollResponseSummaryAggregation, {
                    npsReasonId: npsFollowUpPollId,
                    guideId: this.selectedGuide.id,
                    allPollIds: pollIds,
                    last90Days: true,
                });
            } else {
                guideResponseAggData = await runAggregationOnce(ImportGuidePollResponseSummaryAggregation, {
                    guideId: this.selectedGuide.id,
                    type: this.type,
                    pollIdToCount: pollIds?.length ? pollIds[0] : '',
                    last90Days: true,
                });
            }

            let pollResponseCount = null;
            const pollResponseData = [];
            guideResponseAggData.forEach((response) => {
                const keys = Object.keys(response);
                if (keys.length === 1 && keys[0] === 'count') {
                    pollResponseCount = roundResponseCount(response.count);
                } else {
                    pollResponseData.push(response);
                }
            });

            this.selectedGuideResponses = this.formatResponses(pollResponseData);
            this.selectedGuideResponseCount = pollResponseCount;
            this.$emit('poll-count-update', pollResponseCount);
        },
        changePollQuestion (chosenPoll) {
            this.selectedPollQuestion = chosenPoll;
            this.$emit('poll-selected', this.selectedPollQuestion);
            this.updatePollResponsesCard();
        },
        async assignPollQuestion (row) {
            try {
                await Promise.all([
                    getSourceConfig({ domain: 'Internal', objectName: 'Guide', objectId: row.id }),
                    getGuideFieldMapping({ guideId: row.id }),
                ]).then(([sourceConfig, fieldMappings]) => {
                    const fieldMapping = fieldMappings[0];
                    this.selectedGuide = {
                        ...row,
                        fieldMappingPollId: fieldMapping.subObjectId,
                        sourceConfigId: sourceConfig.id,
                        pollIdsToSendToAI: sourceConfig.subObjectIdsToSendToAI,
                        sendToAIConfirmed: sourceConfig.sendToAIConfirmed,
                        autoCreateStartTime: sourceConfig.startTime,
                        polls: row.polls.map((poll) => ({
                            ...poll,
                            question: formatPollQuestion(poll?.question),
                            analyze: aiAnalyzeFlag(
                                sourceConfig.sendToAIConfirmed,
                                sourceConfig.subObjectIdsToSendToAI,
                                poll.id,
                            ),
                        })),
                    };
                    this.$emit('guide-selected', this.selectedGuide);
                    const selectedPoll = row.validPolls.find((poll) => poll.id === fieldMapping.subObjectId);
                    this.selectedPollQuestion = selectedPoll || row.validPolls[0];
                });
            } catch {
                this.selectedPollQuestion = row.validPolls[0];
                this.selectedGuide = {
                    ...row,
                    pollIdsToSendToAI: [],
                    sendToAIConfirmed: false,
                    polls: row.polls.map((poll) => ({
                        ...poll,
                        analyze: null,
                    })),
                };
            }
        },
        resetSelectedGuide () {
            this.selectedGuide = null;
            this.selectedGuideResponses = [];
            this.selectedGuideResponseCount = null;
            this.selectedPollQuestion = null;
            this.$emit('poll-count-update', null);
            this.$emit('guide-selected', null);
            this.$emit('poll-selected', null);
        },
        formatResponses (responses) {
            return responses.map((response) => {
                const responsePoll = this.selectedGuide.polls.find((poll) => poll.id === response.pollId);
                if (responsePoll?.attributes?.type === 'PickList') {
                    const responseValue = responsePoll.idResponses?.[response.pollResponse];

                    return {
                        ...response,
                        pollResponse: responseValue || 'Deleted option',
                    };
                }

                return response;
            });
        },
    },
};
</script>

<style scoped lang="less">
.import-feedback-drawer-guide-step {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background-color: var(--palette-gray-0);
    width: 95%;
    margin: 0 auto;
    border: 1px solid var(--palette-gray-40);
    padding: 24px 32px;
    border-radius: 3px;
    height: calc(100vh - 224px);
    overflow: hidden;
    &__header {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 16px;
    }
    &__title {
        font-size: 22px;
        font-weight: 600;
        line-height: 130%;
    }
    &__filters {
        margin-bottom: 16px;
    }
    &__content {
        height: 90%;
        width: 100%;
        display: flex;
        flex-direction: row;
        gap: 10px;
        :deep(.pendo-table) {
            transition-property: none;
            transition-duration: none;
            height: calc(100vh - 450px) !important;
            &__body {
                height: calc(100vh - 537px) !important;
            }
        }
        &--nps {
            :deep(.pendo-table) {
                transition-property: none;
                transition-duration: none;
                height: calc(100vh - 450px - 56px) !important;
                &__body {
                    height: calc(100vh - 537px - 56px) !important;
                }
            }
        }
    }
    &__selected-wrapper {
        width: 34%;
    }
    &__table {
        width: 65%;
        &--loading {
            :deep(.pendo-table__scroll-container) {
                min-height: 250px;
            }
        }
    }

    &__autocreate {
        display: flex;
        align-items: center;
        gap: 0;

        :deep(.pendo-icon) {
            padding-right: 3px;
        }
        &--disabled {
            color: var(--palette-gray-70);
        }
    }
}
</style>
